<script setup lang="ts">
import { isInstitutionType } from '@shared/composables/useAppContext';
import { ContractStatus } from '@shared/stores/user';
import type { LoginUserToken } from '@shared/stores/user';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import KgrIcon from '@shared/components/KgrIcon.vue';
import KgrTwoFactorVerify from '@shared/components/KgrTwoFactorVerify.vue';
import { SphSpinner } from '@kigaroo/components/base/spinner';
import { getIsCustomizedApp } from '@shared/appConfig';
import { initApp } from '@shared/composables/useAppInit';
import { openPrivacyPdf } from '@shared/composables/useOpenFile';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalePath } from '@shared/composables/useLocalePath';
import { useUserStore } from '@shared/stores/user';

// Shared login form — port of customer-apps LoginFormComponent (kgr-login-form). Used by
// home/LoginView (with redirect) and user/UserLoginView (without). Without `redirectTo` the
// component does NOT navigate after a successful login (like Angular onLoginSuccess without
// redirectUrl/redirectCommands) and only emits `loginResult`.
const props = defineProps<{
    redirectTo?: string
    /**
     * Angular `@Input() organizationId` — restrict the login to this institution. Takes precedence
     * over the build's own `organization` (`organizationId ?? this.organization?.id`); club-offering
     * passes the `:clubId` of the current route.
     */
    organizationId?: number | null
    /** Angular `@Input() presetUsername` — initial value of the username field, nothing more. */
    presetUsername?: string
}>();
const emit = defineEmits<{ loginResult: [success: boolean] }>();

const TRUSTED_DEVICE_TOKEN_KEY = 'kgr_trusted_device_token';

const router = useRouter();
const route = useRoute();
const localePath = useLocalePath();
const { t } = useI18n();
const userStore = useUserStore();

const username = ref(props.presetUsername ?? '');
const password = ref('');
const showPassword = ref(false);
const isLoading = ref(false);
const errorBadCredentials = ref(false);
const errorOffline = ref(false);
const errorUnexpected = ref(false);
const errorUnexpectedServer = ref(false);
// Angular `treatAllControlsAsDirty`: set on every submit attempt so the required-field errors
// become visible even though the controls are still pristine.
const submitAttempted = ref(false);
const twoFactorToken = ref<string | null>(null);
const isLoggingIn = ref(false);
const selectableTokens = ref<LoginUserToken[]>([]);
const errorSelect = ref(false);

const privacyUrl = window.kgrDynamicConfig.privacyUrl ?? null;
const isCustomizedApp = getIsCustomizedApp();

// Angular splits this sentence by institution type (`*kgrIfInstitutionType`): clubs address the
// member informally ("deiner Daten findest du"), kindergartens formally ("Ihrer Daten finden Sie").
// Two separate XLF keys, not one.
const privacyKeypath
    = isInstitutionType('club')
        ? 'Informationen zur Verarbeitung deiner Daten findest du in unseren {link}.'
        : 'Informationen zur Verarbeitung Ihrer Daten finden Sie in unseren {link}.';

// Angular Validators.required on both controls; the message comes from the styleguide's
// FormValidationErrorHandler (`required: 'Angabe benötigt'`).
const usernameErrors = computed(() => (submitAttempted.value && !username.value ? [t('Angabe benötigt')] : []));
const passwordErrors = computed(() => (submitAttempted.value && !password.value ? [t('Angabe benötigt')] : []));

function clearErrors() {
    errorBadCredentials.value = false;
    errorOffline.value = false;
    errorUnexpected.value = false;
    errorUnexpectedServer.value = false;
}

async function onLogin() {
    submitAttempted.value = true;
    if (!username.value || !password.value) {
        return;
    }

    clearErrors();
    isLoading.value = true;

    try {
        const { apiHost, apiPath } = window.kgrDynamicConfig;
        const formData = new FormData();
        formData.append('_username', username.value);
        formData.append('_password', password.value);

        const headers: Record<string, string> = {};
        const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_TOKEN_KEY);
        if (trustedDeviceToken) {
            headers['X-Trusted-Device'] = trustedDeviceToken;
        }

        const response = await fetch(`${apiHost}${apiPath}/login`, { method: 'POST', body: formData, headers });

        // 404/401/403 all map to bad credentials (1:1 Angular login-form error switch).
        if (response.status === 404 || response.status === 401 || response.status === 403) {
            errorBadCredentials.value = true;
            return;
        }
        if (!response.ok) {
            errorUnexpectedServer.value = true;
            return;
        }

        const body = await response.json();

        if (body?.requires_2fa === true) {
            twoFactorToken.value = body.two_factor_token;
            return;
        }

        if (body?.requires_2fa_setup === true) {
            const setupTokens: LoginUserToken[] = body.tokens ?? [];
            if (setupTokens.length === 0) {
                errorUnexpected.value = true;
                return;
            }
            isLoggingIn.value = true;
            userStore.setLoginUserTokens(setupTokens);
            const loginToken = setupTokens[0];
            userStore.setToken(loginToken.token);
            userStore.setInstitutionId(loginToken.institutionId);
            const locale = route.params.locale as string;
            router.replace(`/${locale}/preferences/two-factor-auth`);
            return;
        }

        const tokens: LoginUserToken[] = body;

        const { organization, organizationIds, federationIds } = window.kgrDynamicConfig;
        let filteredTokens: LoginUserToken[] = [];
        let isFiltered = false;

        // Angular: `organizationId = organizationId ?? this.organization?.id` — the explicit input wins.
        const organizationId = props.organizationId ?? organization;
        if (organizationId) {
            isFiltered = true;
            filteredTokens = [...filteredTokens, ...tokens.filter((t) => t.institutionId === organizationId)];
        }
        // Angular filtert zusaetzlich ueber die Whitelist `organizationIds` (Array) — der
        // White-Label-Build eines Vereins laesst nur Logins in dessen Institutionen zu. Ein Client
        // kann mehrere haben (tc-blau-weiss-berlin: [112, 72]), deshalb ein Array und kein Einzelwert.
        if (organizationIds && organizationIds.length > 0) {
            isFiltered = true;
            filteredTokens = [...filteredTokens, ...tokens.filter((t) => organizationIds.includes(t.institutionId))];
        }
        if (federationIds && federationIds.length > 0) {
            isFiltered = true;
            filteredTokens = [
                ...filteredTokens,
                ...tokens.filter((t) => t.federationId !== undefined && federationIds.includes(t.federationId)),
            ];
        }
        const availableTokens = isFiltered ? filteredTokens.filter((t, i, arr) => arr.findIndex((u) => u.token === t.token) === i) : tokens;

        userStore.setLoginUserTokens(availableTokens);
        await presentTokens(availableTokens);
    } catch {
        errorOffline.value = true;
    } finally {
        isLoading.value = false;
    }
}

// Decides between auto-login and showing the account selection, mirroring the
// Angular login form: auto-login only for a single token with a running (or
// unknown) contract, otherwise let the user pick.
async function presentTokens(tokens: LoginUserToken[]) {
    if (tokens.length === 0) {
        errorBadCredentials.value = true;
        return;
    }

    const single = tokens[0];
    const canAutoLogin
        = tokens.length === 1
            && (single.contractStatus === null || single.contractStatus === undefined || single.contractStatus === ContractStatus.running);

    if (canAutoLogin) {
        // Angular login-form.component.ts:158 — der Auto-Login-Zweig meldet ein ungueltiges Token
        // als `unexpectedError`, der Auswahl-Zweig unten als `States.error` der Select-Komponente.
        if (!(await completeLogin(single))) {
            errorUnexpected.value = true;
        }
    } else {
        selectableTokens.value = tokens;
    }
}

// Gibt `false` zurueck, wenn der Token die JWT-Pruefung nicht besteht — 1:1 Angulars
// `authenticationService.loginByToken()`, das in BEIDEN Einbaustellen der Select-Komponente
// (login-form.component.ts:158, select-login-user.component.ts:51) vor dem Wechsel validiert.
// `setToken()` direkt zu setzen wuerde einen abgelaufenen Geraete-Token blind uebernehmen.
async function completeLogin(loginToken: LoginUserToken): Promise<boolean> {
    isLoggingIn.value = true;
    if (!loginToken.token || !userStore.loginByToken(loginToken)) {
        isLoggingIn.value = false;
        return false;
    }
    await initApp();

    emit('loginResult', true);

    // Like Angular onLoginSuccess: only navigate when a redirect target is set.
    if (props.redirectTo) {
        router.replace(props.redirectTo);
    } else {
        isLoggingIn.value = false;
    }
    return true;
}

async function onSelectToken(loginToken: LoginUserToken) {
    errorSelect.value = false;
    if (!(await completeLogin(loginToken))) {
        errorSelect.value = true;
    }
}

function onCancelSelect() {
    selectableTokens.value = [];
    errorSelect.value = false;
    void userStore.logout();
    emit('loginResult', false);
}

async function onTwoFactorSuccess(tokens: LoginUserToken[]) {
    twoFactorToken.value = null;
    if (tokens.length === 0) {
        errorUnexpected.value = true;
        return;
    }
    userStore.setLoginUserTokens(tokens);
    await presentTokens(tokens);
}
</script>

<template>
    <div
        v-if="isLoggingIn"
        class="kgr-row kgr-text kgr-text--centered"
    >
        <SphSpinner size="lg" />
    </div>

    <KgrTwoFactorVerify
        v-else-if="twoFactorToken"
        :two-factor-token="twoFactorToken"
        @success="onTwoFactorSuccess"
        @cancel="twoFactorToken = null"
    />

    <div v-else-if="selectableTokens.length > 0">
        <h2 class="kgr-headline kgr-headline--h3 kgr-row kgr-row--extraSmallMargin">
            {{ $t('Wähle einen Account') }}
        </h2>
        <p class="kgr-paragraph">
            <i18n-t
                keypath="Es existieren folgende Accounts für die E-Mail-Adresse {email}. Bitte wählen:"
                tag="span"
            >
                <template #email>
                    {{ username }}
                </template>
            </i18n-t>
        </p>
        <div
            v-if="errorSelect"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Es ist leider ein unerwarteter Fehler aufgetreten.') }}
        </div>
        <hr class="kgr-separator kgr-separator--light" />
        <template
            v-for="loginToken in selectableTokens"
            :key="loginToken.token"
        >
            <button
                type="button"
                class="kgr-link kgr-clickable kgr-clickable--touchy"
                @click="onSelectToken(loginToken)"
            >
                <i18n-t
                    keypath="Anmelden bei {institution} als {name}"
                    tag="span"
                >
                    <template #institution>
                        <!-- Anfuehrungszeichen gehoeren INS Bold-Tag: Angulars XLF hat
                             START_BOLD_TEXT -> &quot; -> INTERPOLATION. -->
                        <b class="kgr-text kgr-text--bold">"{{ loginToken.institutionName }}"</b>
                    </template>
                    <template #name>
                        <b class="kgr-text kgr-text--bold">{{ loginToken.name }}</b>
                    </template>
                </i18n-t>
            </button>
            <hr class="kgr-separator kgr-separator--light" />
        </template>
        <button
            type="button"
            class="kgr-link"
            @click="onCancelSelect"
        >
            {{ $t('Benutzer ausloggen') }}
        </button>
    </div>

    <form
        v-else
        @submit.prevent="onLogin"
    >
        <div
            v-if="errorBadCredentials"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            <div class="kgr-row">
                {{ $t('Der Benutzername oder das Passwort ist falsch. Bitte Angaben überprüfen oder Passwort zurück setzen.') }}
            </div>
            <RouterLink
                :to="{ path: localePath('user/password/reset'), query: username ? { email: username } : undefined }"
                class="kgr-button"
            >
                {{ $t('Passwort zurücksetzen') }}
            </RouterLink>
        </div>
        <div
            v-if="errorOffline"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Es konnte keine Verbindung zum Server hergestellt werden.') }}
        </div>
        <div
            v-if="errorUnexpected"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Es ist leider ein unerwarteter Fehler aufgetreten.') }}
        </div>
        <div
            v-if="errorUnexpectedServer"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Es ist leider ein unerwarteter Serverfehler aufgetreten.') }}
        </div>

        <div class="kgr-grid kgr-grid--full kgr-grid--large-fit kgr-grid--gutters kgr-grid--bottom kgr-row">
            <div class="kgr-grid__cell">
                <label
                    class="kgr-inputGroup"
                    :class="{ 'is-invalid': usernameErrors.length > 0 }"
                >
                    <span class="kgr-inputGroup__label">{{ $t('E-Mail') }} *</span>
                    <div
                        v-for="error in usernameErrors"
                        :key="error"
                        class="kgr-inputGroup__text kgr-inputGroup__text--error"
                    >
                        {{ error }}
                    </div>
                    <input
                        v-model="username"
                        type="email"
                        class="kgr-input"
                        autocomplete="email"
                        :disabled="isLoading"
                        @input="clearErrors"
                    />
                </label>
            </div>
            <div class="kgr-grid__cell">
                <label
                    class="kgr-inputGroup"
                    :class="{ 'is-invalid': passwordErrors.length > 0 }"
                >
                    <span class="kgr-inputGroup__label">{{ $t('Passwort') }} *</span>
                    <div
                        v-for="error in passwordErrors"
                        :key="error"
                        class="kgr-inputGroup__text kgr-inputGroup__text--error"
                    >
                        {{ error }}
                    </div>
                    <div class="kgr-inputIcon">
                        <input
                            v-model="password"
                            :type="showPassword ? 'text' : 'password'"
                            class="kgr-inputIcon__input kgr-input"
                            autocomplete="current-password"
                            :disabled="isLoading"
                            @input="clearErrors"
                        />
                        <button
                            type="button"
                            class="kgr-inputIcon__icon"
                            :aria-label="showPassword ? $t('Passwort verbergen') : $t('Passwort anzeigen')"
                            @click="showPassword = !showPassword"
                        >
                            <KgrIcon :name="showPassword ? 'visibility-on' : 'visibility-off'" />
                        </button>
                    </div>
                </label>
            </div>
            <div class="kgr-grid__cell kgr-grid__cell--bottom">
                <button
                    type="submit"
                    class="kgr-button kgr-button--block kgr-button--accent"
                    :disabled="isLoading"
                >
                    {{ isLoading ? $t('Anmelden …') : $t('Anmelden') }}
                </button>
            </div>
        </div>

        <p class="kgr-paragraph kgr-row">
            <i18n-t
                :keypath="privacyKeypath"
                tag="span"
            >
                <template #link>
                    <a
                        v-if="isCustomizedApp && privacyUrl"
                        :href="privacyUrl"
                        target="_blank"
                        rel="noopener"
                        class="kgr-link"
                    >{{ $t('Datenschutzhinweisen') }}</a>
                    <button
                        v-else
                        type="button"
                        class="kgr-link kgr-clickable"
                        @click="openPrivacyPdf()"
                    >
                        {{ $t('Datenschutzhinweisen') }}
                    </button>
                </template>
            </i18n-t>
        </p>

        <RouterLink
            :to="localePath('user/password/reset')"
            class="kgr-link kgr-clickable kgr-clickable--touchy"
        >
            {{ $t('Passwort vergessen') }}
        </RouterLink>
    </form>
</template>

<style scoped lang="scss">
form {
    max-width: 1000px;
}
</style>
